import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, MapPin, CheckCircle2 } from 'lucide-react';
import { projectsData } from '../data/projectsData';

const ProjectDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    // Find project by ID (convert string id to number for comparison)
    const project = projectsData.find(p => p.id === parseInt(id));

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!project) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold mb-4">Project Not Found</h2>
                    <button
                        onClick={() => navigate('/projects')}
                        className="text-[#008000] hover:underline"
                    >
                        Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-20 min-h-screen bg-gray-50">
            {/* Hero Header */}
            <div className="relative h-[60vh] min-h-[400px]">
                <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 flex items-center">
                    <div className="max-w-7xl mx-auto px-4 w-full text-white">
                        <Link
                            to="/projects"
                            className="inline-flex items-center text-white/90 mb-6 hover:text-white hover:underline transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back to Projects
                        </Link>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${project.status === 'Completed' ? 'bg-[#008000] text-white' : 'bg-orange-500 text-white'
                                }`}>
                                {project.status}
                            </span>
                            <h1 className="text-4xl md:text-6xl font-bold mb-4">{project.title}</h1>
                            <div className="flex flex-wrap gap-6 text-sm md:text-base text-white/90">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-[#008000]" />
                                    {project.location}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-[#008000]" />
                                    {project.date}
                                </div>
                                <div className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-[#008000]" />
                                    {project.client}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-20">
                <div className="grid lg:grid-cols-3 gap-12">

                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-12">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
                            <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                                {project.longDescription}
                            </p>
                        </motion.div>

                        {/* Gallery */}
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">Gallery</h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                {project.gallery && project.gallery.map((img, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: idx * 0.1 }}
                                        viewport={{ once: true }}
                                        className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow h-64"
                                    >
                                        <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Details */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 sticky top-24">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Project Details</h3>

                            <div className="space-y-6">
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Category</p>
                                    <p className="font-semibold text-gray-800">{project.category}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 mb-1">Total Area</p>
                                    <p className="font-semibold text-gray-800">{project.area}</p>
                                </div>

                                <div className="pt-6 border-t border-gray-100">
                                    <h4 className="font-bold text-gray-900 mb-4">Key Amenities</h4>
                                    <ul className="space-y-3">
                                        {project.amenities && project.amenities.map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                                                <CheckCircle2 className="w-4 h-4 text-[#008000] mt-0.5" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="mt-8 pt-8 border-t border-gray-100">
                                <Link
                                    to="/contact"
                                    className="block w-full bg-[#008000] text-white text-center font-bold py-3 px-6 rounded-lg hover:bg-[#006400] transition-colors shadow-md hover:shadow-lg"
                                >
                                    Start a Similar Project
                                </Link>
                            </div>

                        </div>
                    </div>

                </div>
            </div>

        </div>
    );
};

export default ProjectDetail;
